<script setup lang="ts">
import QitBtn from "@/shared/components/buttons/qit-button.vue";
import { useAbilityRoute } from "@/shared/environment/ability-route.ts";
import { CommonRouteAliases } from "@/shared/environment/common-route-aliases.ts";
import { useRouter } from "vue-router";
import { SearchContext } from "@/shared/search/search.types.ts";
import { useProductNames } from "@/abilities/product-and-asset/composables/product-names.ts";
import { useAssetNames } from "@/abilities/product-and-asset/composables/asset-names.ts";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useLoadConfigProductAndAsset } from "@/shared/configurations/product-and-asset/load-product-and-asset-settings.ts";
import Banner from "@/shared/banner/banner.vue";
import { useAiAssistantAccess } from "../ai-assistant/ai-assistant-access.ts";

const props = defineProps<{
    context: SearchContext;
    searchResultStatus: "tooFew" | "tooMany" | "none";
    hideAiAssistant?: boolean;
}>();

defineEmits<{ onOpenAiAssistant: []; onOpenNeedQualification: [] }>();

const { t } = useI18n();
const router = useRouter();
const { getPathToChildWithoutParents } = useAbilityRoute();
const { assetsActivated } = useLoadConfigProductAndAsset();
const goToProducts = useProductNames("goToProducts");
const productName = useProductNames("singular");
const assetName = useAssetNames("singular");
const { hasAccess } = useAiAssistantAccess();

const needQualificationWizardLabel = computed(() => {
    if (assetsActivated.value) {
        const product = productName.value ?? t("shared.Product");
        const asset = assetName.value ?? t("shared.Asset");
        return t("shared.Select {productName} or {assetName}", { productName: product, assetName: asset });
    }

    return goToProducts.value;
});

const isAiAssistantAllowed = computed(() => {
    return hasAccess.value && !props.hideAiAssistant;
});

const goToAIAssistant = () => {
    const pathToChild = getPathToChildWithoutParents(CommonRouteAliases.aiAssistant, {
        ["product"]: props.context.product ?? "",
        ["asset"]: props.context.asset ?? "",
    });
    router.push(pathToChild);
};
</script>

<template>
    <Banner>
        <template #icon>
            <q-icon name="fa-regular fa-circle-info" color="warning" size="40px" />
        </template>
        <template #content>
            <template v-if="searchResultStatus === 'tooMany'">
                {{
                    $t(
                        "shared.Your search returned too many hits to display Select a different search context or use filters"
                    )
                }}
            </template>
            <template v-else-if="searchResultStatus === 'tooFew'">
                <div class="col">
                    <div>
                        {{ $t("shared.Cant find what youre looking for") }}
                    </div>
                    <div v-if="isAiAssistantAllowed">
                        {{ $t("shared.Select a different search context or ask our AI Assistant") }}
                    </div>
                    <div v-else>{{ $t("shared.Select a different search context") }}</div>
                </div>
            </template>
            <template v-else-if="searchResultStatus === 'none'">
                <div class="col">
                    <div>{{ $t("shared.No matching results found") }}</div>
                    <div v-if="isAiAssistantAllowed">
                        {{ $t("shared.Your search returned no hits") }}
                        {{ $t("shared.Select a different search context or ask our AI Assistant") }}
                    </div>
                    <div v-else>{{ $t("shared.Select a different search context") }}</div>
                </div>
            </template>
        </template>
        <template #actions>
            <div v-if="isAiAssistantAllowed">
                <QitBtn
                    data-qs="open-ai-assistant"
                    icon="fak fa-regular fa-qit-ai-assistant-regular icon-size-m"
                    color="secondary"
                    :label="$t('ai-assistant.Open AI Assistant')"
                    @click="goToAIAssistant"
                />
            </div>
            <div>
                <QitBtn
                    data-qs="select-product-or-asset"
                    :label="needQualificationWizardLabel"
                    color="primary"
                    @click="$emit('onOpenNeedQualification')"
                />
            </div>
        </template>
    </Banner>
</template>
